import logger from '../utils/logger.js';
import { getProxyUrl } from '../utils/image-proxy-processor.js';
import { teardownGroupContextNav } from '../utils/group-context-nav.js';

/**
 * /groups/invites/:token — preview and accept/decline group invitations.
 */
class GroupInviteAcceptPage extends HTMLElement {
  constructor() {
    super();
    this.token = null;
    this.preview = null;
    this.currentUser = null;
    this.loading = true;
    this.error = null;
    this.submitting = false;
    this._headerProxy = null;
    this._avatarProxy = null;
    this._onAuthSuccess = this.handleAuthSuccess.bind(this);
  }

  static get observedAttributes() {
    return ['invite-token'];
  }

  attributeChangedCallback(name, _old, value) {
    if (name === 'invite-token' && value !== this.token) {
      this.token = value;
      this.load();
    }
  }

  connectedCallback() {
    teardownGroupContextNav();
    this.token = this.getAttribute('invite-token');
    document.addEventListener('auth-status-loaded', this._onAuthSuccess);
    document.addEventListener('auth-success', this._onAuthSuccess);
    document.addEventListener('user-logged-out', this._onAuthSuccess);
    this.syncCurrentUser();
    this.load();
  }

  disconnectedCallback() {
    document.removeEventListener('auth-status-loaded', this._onAuthSuccess);
    document.removeEventListener('auth-success', this._onAuthSuccess);
    document.removeEventListener('user-logged-out', this._onAuthSuccess);
  }

  syncCurrentUser() {
    this.currentUser = window.currentUser || null;
  }

  handleAuthSuccess(e) {
    this.currentUser = e?.detail?.user || window.currentUser || null;
    this.load();
  }

  escapeHtml(value) {
    if (!value) return '';
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  async load() {
    if (!this.token || !window.API) {
      return;
    }
    this.loading = true;
    this.error = null;
    this.render();
    try {
      this.preview = await window.API.request(`/groups/invites/${this.token}/preview`);
      await this.loadImageProxies();
    } catch (e) {
      this.preview = null;
      this.error = e.message || 'Could not load invitation';
      logger.warn('GroupInviteAcceptPage load failed', e);
    } finally {
      this.loading = false;
      this.render();
      this.attachListeners();
    }
  }

  async loadImageProxies() {
    this._headerProxy = null;
    this._avatarProxy = null;
    const uid = this.currentUser?.id;
    if (!uid || !this.preview?.group) {
      return;
    }
    if (this.preview.group.header_image_url) {
      this._headerProxy = await getProxyUrl(this.preview.group.header_image_url, uid, null);
    }
    if (this.preview.group.avatar_image_url) {
      this._avatarProxy = await getProxyUrl(this.preview.group.avatar_image_url, uid, null);
    }
  }

  setAuthReturnTo() {
    const path = `/groups/invites/${this.token}`;
    if (window.router?.setAuthReturnTo) {
      window.router.setAuthReturnTo(path);
    } else {
      sessionStorage.setItem('fotAuthReturnTo', path);
    }
  }

  async openLogin() {
    this.setAuthReturnTo();
    if (window.router?.openAuthModal) {
      await window.router.openAuthModal('login');
    }
  }

  async openRegister() {
    this.setAuthReturnTo();
    if (window.router?.openAuthModal) {
      await window.router.openAuthModal('register', {
        prefillEmail: this.preview?.invite_email || null
      });
    }
  }

  async acceptInvite() {
    if (this.submitting) return;
    this.submitting = true;
    this.render();
    try {
      const result = await window.API.request(`/groups/invites/${this.token}/accept`, {
        method: 'POST'
      });
      if (window.router?.navigate) {
        window.router.navigate(`/groups/${result.group_id}`);
      }
    } catch (e) {
      this.error = e.message || 'Could not accept invitation';
      this.submitting = false;
      this.render();
      this.attachListeners();
    }
  }

  async declineInvite() {
    if (this.submitting) return;
    this.submitting = true;
    this.render();
    try {
      await window.API.request(`/groups/invites/${this.token}/decline`, {
        method: 'POST'
      });
      if (window.router?.navigate) {
        window.router.navigate('/groups');
      }
    } catch (e) {
      this.error = e.message || 'Could not decline invitation';
      this.submitting = false;
      this.render();
      this.attachListeners();
    }
  }

  async logout() {
    try {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    } catch (e) {
      logger.warn('Logout failed on invite page', e);
    }
    this.currentUser = null;
    window.currentUser = null;
    document.dispatchEvent(new CustomEvent('user-logged-out', { bubbles: true }));
    this.load();
  }

  attachListeners() {
    this.querySelector('[data-action="login"]')?.addEventListener('click', () => this.openLogin());
    this.querySelector('[data-action="register"]')?.addEventListener('click', () => this.openRegister());
    this.querySelector('[data-action="accept"]')?.addEventListener('click', () => this.acceptInvite());
    this.querySelector('[data-action="decline"]')?.addEventListener('click', () => this.declineInvite());
    this.querySelector('[data-action="logout"]')?.addEventListener('click', () => this.logout());

    const root = this.querySelector('.group-invite-accept');
    const bannerColor = this.preview?.group?.header_banner_color || '';
    if (root && bannerColor.startsWith('#')) {
      root.style.setProperty('--group-header-color', bannerColor);
    }
    const ban = this.querySelector('.group-shell-banner.has-image');
    if (ban && this._headerProxy) {
      ban.style.backgroundImage = `linear-gradient(120deg, rgba(0,0,0,.35), transparent), url(${this._headerProxy})`;
    }
  }

  renderBanner() {
    const group = this.preview?.group;
    if (!group) return '';

    const bannerClass = ['group-shell-banner'];
    if (this._headerProxy) {
      bannerClass.push('has-image');
    }

    const avatarHtml = this._avatarProxy
      ? `<img class="group-shell-avatar" src="${this._avatarProxy}" alt="" />`
      : '<div class="group-shell-avatar" aria-hidden="true">🌱</div>';

    return `
      <header class="group-shell-header">
        <div class="${bannerClass.join(' ')}"></div>
        <div class="group-shell-heading">
          ${avatarHtml}
          <div class="group-shell-title-block">
            <h1>${this.escapeHtml(group.name)}</h1>
            ${group.description ? `<p>${this.escapeHtml(group.description)}</p>` : ''}
          </div>
        </div>
      </header>
    `;
  }

  renderActions() {
    if (!this.preview) return '';

    const {
      status,
      expired,
      authenticated,
      can_respond,
      mismatch_reason,
      invitee_display
    } = this.preview;
    const inviter = this.preview.inviter?.username || 'Someone';
    const inviteLead = invitee_display
      ? `<strong>${this.escapeHtml(inviter)}</strong> invited <strong>${this.escapeHtml(invitee_display)}</strong> to join this group.`
      : `<strong>${this.escapeHtml(inviter)}</strong> invited someone to join this group.`;

    if (status !== 'pending' || expired) {
      const msg = expired ? 'This invitation has expired.' : `This invitation is ${status}.`;
      return `
        <div class="group-invite-accept-card card">
          <p class="group-invite-accept-lead">${this.escapeHtml(msg)}</p>
          <button type="button" class="btn btn-secondary" data-action="hub">Go to Groups</button>
        </div>
      `;
    }

    if (!authenticated) {
      return `
        <div class="group-invite-accept-card card">
          <p class="group-invite-accept-lead">${inviteLead}</p>
          <p class="group-invite-accept-note">You need a Fun Old Times account to accept or decline.</p>
          <div class="group-invite-accept-actions">
            <button type="button" class="btn btn-primary" data-action="register">Create account</button>
            <button type="button" class="btn btn-secondary" data-action="login">Log in</button>
          </div>
        </div>
      `;
    }

    if (mismatch_reason) {
      return `
        <div class="group-invite-accept-card card">
          <p class="group-invite-accept-lead">${inviteLead}</p>
          <p class="group-invite-accept-note error">${this.escapeHtml(mismatch_reason)}</p>
          <div class="group-invite-accept-actions">
            <button type="button" class="btn btn-secondary" data-action="logout">Log out and switch account</button>
          </div>
        </div>
      `;
    }

    if (can_respond) {
      return `
        <div class="group-invite-accept-card card">
          <p class="group-invite-accept-lead">
            <strong>${this.escapeHtml(inviter)}</strong> invited you to join this group.
          </p>
          <div class="group-invite-accept-actions">
            <button type="button" class="btn btn-primary" data-action="accept" ${this.submitting ? 'disabled' : ''}>
              ${this.submitting ? 'Joining…' : 'Accept'}
            </button>
            <button type="button" class="btn btn-secondary" data-action="decline" ${this.submitting ? 'disabled' : ''}>
              Decline
            </button>
          </div>
        </div>
      `;
    }

    return `
      <div class="group-invite-accept-card card">
        <p class="group-invite-accept-note">This invitation cannot be responded to right now.</p>
      </div>
    `;
  }

  render() {
    if (this.loading) {
      this.innerHTML = `
        <div class="group-invite-accept">
          <div class="loading-state"><div class="spinner"></div><div>Loading invitation…</div></div>
        </div>
      `;
      return;
    }

    if (this.error && !this.preview) {
      this.innerHTML = `
        <div class="group-invite-accept">
          <p class="error">${this.escapeHtml(this.error)}</p>
        </div>
      `;
      return;
    }

    this.innerHTML = `
      <div class="group-invite-accept">
        ${this.renderBanner()}
        ${this.error ? `<p class="error">${this.escapeHtml(this.error)}</p>` : ''}
        ${this.renderActions()}
      </div>
    `;

    this.querySelector('[data-action="hub"]')?.addEventListener('click', () => {
      window.router?.navigate('/groups');
    });
  }
}

customElements.define('group-invite-accept-page', GroupInviteAcceptPage);

export default GroupInviteAcceptPage;
